import React, { useState, useEffect } from 'react';
import './Landing.css';
import { createDocument } from '../../services/firestoreService';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CheckCircle, Star, Zap, Users, Calendar, BarChart3, Shield, Clock, Building, User, Mail, Phone, Globe, Send, LogIn, UserPlus, Menu, X } from 'lucide-react';

const defaultPricing = {
  basic: { monthly: 49, annual: 490 },
  pro: { monthly: 99, annual: 990 },
  enterprise: { monthly: 199, annual: 1990 }
};

const Landing = () => {
  const [form, setForm] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    desiredPlan: 'basic'
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [pricing, setPricing] = useState(defaultPricing);
  const [systemFees, setSystemFees] = useState({ basic: 3, pro: 2, enterprise: 1, enterpriseFreeTransactions: 10 });
  const [serviceCommissions, setServiceCommissions] = useState({});
  const [planFeatures, setPlanFeatures] = useState({});
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [toast, setToast] = useState({ text: '', visible: false });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);



  useEffect(() => {
    // carregar preços em tempo real
    const pricingRef = doc(db, 'systemConfig', 'pricing');
    const prevRef = { current: null };
    const unsubPricing = onSnapshot(pricingRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setPricing({
          basic: { monthly: Number(data.basicMonthly) || defaultPricing.basic.monthly, annual: Number(data.basicAnnual) || defaultPricing.basic.annual },
          pro: { monthly: Number(data.proMonthly) || defaultPricing.pro.monthly, annual: Number(data.proAnnual) || defaultPricing.pro.annual },
          enterprise: { monthly: Number(data.enterpriseMonthly) || defaultPricing.enterprise.monthly, annual: Number(data.enterpriseAnnual) || defaultPricing.enterprise.annual }
        });
        // se preços mudaram, mostrar toast
        if (prevRef.current) {
          const prev = prevRef.current;
          const changed = (
            prev.basic.monthly !== Number(data.basicMonthly) ||
            prev.basic.annual !== Number(data.basicAnnual) ||
            prev.pro.monthly !== Number(data.proMonthly) ||
            prev.pro.annual !== Number(data.proAnnual) ||
            prev.enterprise.monthly !== Number(data.enterpriseMonthly) ||
            prev.enterprise.annual !== Number(data.enterpriseAnnual)
          );
          if (changed) {
            setToast({ text: 'Preços atualizados', visible: true });
            setTimeout(() => setToast({ text: '', visible: false }), 3500);
          }
        }
        prevRef.current = {
          basic: { monthly: Number(data.basicMonthly) || defaultPricing.basic.monthly, annual: Number(data.basicAnnual) || defaultPricing.basic.annual },
          pro: { monthly: Number(data.proMonthly) || defaultPricing.pro.monthly, annual: Number(data.proAnnual) || defaultPricing.pro.annual },
          enterprise: { monthly: Number(data.enterpriseMonthly) || defaultPricing.enterprise.monthly, annual: Number(data.enterpriseAnnual) || defaultPricing.enterprise.annual }
        };
      } else {
        setPricing(defaultPricing);
      }
    }, (err) => {
      console.error('Pricing listener error:', err);
      // Fallback to defaults if there's an error
      setPricing(defaultPricing);
    });

    // Carregar taxas e comissões definidas pelo root (Mercado Pago config)
    const mercadoRef = doc(db, 'systemConfig', 'mercadoPago');
    const unsubMercado = onSnapshot(mercadoRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setSystemFees({
          basic: Number(data.systemFeeBasic) || 3,
          pro: Number(data.systemFeePro) || 2,
          enterprise: Number(data.systemFeeEnterprise) || 1,
          enterpriseFreeTransactions: Number(data.enterpriseFreeTransactions) || 10
        });
        setServiceCommissions(data.defaultServiceCommissions || {});
        setPlanFeatures(data.planFeatures || {});
      } else {
        setSystemFees({ basic: 3, pro: 2, enterprise: 1, enterpriseFreeTransactions: 10 });
        setServiceCommissions({});
        setPlanFeatures({});
      }
    }, (err) => {
      // Evitar spam de erros nos logs quando a leitura é negada (p.ex. visitantes anônimos)
      if (err && err.code === 'permission-denied') {
        console.warn('MercadoPago listener: leitura negada — usando valores padrão.');
      } else {
        console.error('MercadoPago listener error:', err);
      }
      setSystemFees({ basic: 3, pro: 2, enterprise: 1, enterpriseFreeTransactions: 10 });
    });

    return () => {
      if (unsubPricing) unsubPricing();
      if (unsubMercado) unsubMercado();
    };
  }, []);

  useEffect(() => {
    // Smooth scroll com velocidade controlada
    const handleSmoothScroll = (e) => {
      const target = e.target;
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const targetId = target.getAttribute('href');
        const targetElement = document.querySelector(targetId);

        if (targetElement) {
          const startPosition = window.pageYOffset;
          const targetPosition = targetElement.offsetTop - 80; // Offset para o header
          const distance = targetPosition - startPosition;
          const duration = 1200; // 1.2 segundos para scroll mais lento
          let startTime = null;

          const animation = (currentTime) => {
            if (startTime === null) startTime = currentTime;
            const timeElapsed = currentTime - startTime;
            const progress = Math.min(timeElapsed / duration, 1);

            // Easing function para movimento mais natural
            const easeInOutCubic = progress < 0.5
              ? 4 * progress * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 3) / 2;

            window.scrollTo(0, startPosition + distance * easeInOutCubic);

            if (progress < 1) {
              requestAnimationFrame(animation);
            }
          };

          requestAnimationFrame(animation);
        }
      }
    };

    // Adicionar event listeners para todos os links de navegação
    const navLinks = document.querySelectorAll('a[href^="#"]');
    navLinks.forEach(link => {
      link.addEventListener('click', handleSmoothScroll);
    });

    return () => {
      navLinks.forEach(link => {
        link.removeEventListener('click', handleSmoothScroll);
      });
    };
  }, []);

  const formatCurrency = (value) => {
    const n = Number(value) || 0;
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 });
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.companyName || !form.contactName || !form.email) {
      setMessage({ type: 'error', text: 'Por favor preencha os campos obrigatórios.' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const payload = {
        companyName: form.companyName,
        contactName: form.contactName,
        email: form.email,
        phone: form.phone || '',
        website: form.website || '',
        desiredPlan: form.desiredPlan,
        status: 'pending',
        createdAt: new Date()
      };

      const res = await createDocument('companyPreapprovals', payload);
      if (res.error) throw new Error(res.error);

      setMessage({ type: 'success', text: 'Pré-cadastro enviado com sucesso. Aguarde aprovação.' });
      setForm({ companyName: '', contactName: '', email: '', phone: '', website: '', desiredPlan: 'basic' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erro ao enviar pré-cadastro. Tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landing-root">
      <nav className="landing-nav">
        <div className="nav-inner">
          <div className="brand">
            <img src="/logo192.png" alt="SDC Turismo - Logo da empresa de gestão turística" className="brand-logo" />
            <span className="brand-title">SDC Turismo</span>
          </div>

          {/* Desktop Navigation */}
          <div className="nav-actions desktop-nav">
            <a href="/login" className="nav-link">
              <LogIn size={18} />
              <span>Entrar</span>
            </a>
            <a href="#prereg" className="btn-primary-modern">
              <UserPlus size={18} />
              <span>Solicitar Acesso</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-nav-content">
            <a href="/login" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <LogIn size={20} />
              <span>Entrar</span>
            </a>
            <a href="#prereg" className="mobile-btn-primary" onClick={() => setMobileMenuOpen(false)}>
              <UserPlus size={20} />
              <span>Solicitar Acesso</span>
            </a>
          </div>
        </div>
      </nav>

      <header className="landing-hero modern-hero">
        <div className="landing-inner">
          <div className="hero-grid">
            <div className="hero-content">
              <h1>Transforme sua pousada com tecnologia de ponta</h1>
              <p className="hero-lead">Sistema completo de gestão para turismo: reservas inteligentes, pagamentos automáticos e relatórios precisos. Tudo que você precisa para crescer seu negócio.</p>
              <div className="hero-ctas">
                <a href="#prereg" className="btn-cta">
                  <UserPlus size={18} />
                  Começar Agora
                </a>
                <a href="#features" className="btn-link">Ver Recursos</a>
              </div>
              <div className="trust">Mais de 500 empresas já confiam no SDC Turismo</div>
            </div>

            <div className="hero-visual">
              <div className="card-visual logo-showcase">
                <img src="/logo512.png" alt="Logo SDC Turismo - Sistema de gestão para turismo" className="visual-logo" />
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="landing-plans">
        <div className="plans-inner">
          <div className="plans-toggle">
            <button className={`period ${billingPeriod === 'monthly' ? 'active' : ''}`} onClick={() => setBillingPeriod('monthly')}>Mensal</button>
            <button className={`period ${billingPeriod === 'annual' ? 'active' : ''}`} onClick={() => setBillingPeriod('annual')}>Anual</button>
          </div>

          <div className="plans-preview">
            <div className="plan" style={{ '--i': 0 }}>
              <div className="plan-header">
                <CheckCircle className="plan-icon" />
                <h3>Básico</h3>
              </div>
              <div className="price">{billingPeriod === 'monthly' ? formatCurrency(pricing.basic.monthly) : formatCurrency(pricing.basic.annual)}{billingPeriod === 'monthly' ? '/mês' : '/ano'}</div>
              <p>Ideal para empresas iniciantes. <span className="fee-badge fee-basic">{systemFees.basic}%</span> <small className="fee-label">Taxa do sistema</small>.</p>
              <ul className="plan-features">
                <li><CheckCircle size={16} /> {`Até ${planFeatures?.basic?.maxReservationsPerMonth ?? 100} reservas/mês`}</li>
                <li><CheckCircle size={16} /> Gestão básica de clientes</li>
                {planFeatures?.basic?.advancedReports ? <li key="basic-adv"><CheckCircle size={16} /> Relatórios avançados</li> : <li key="basic-simple">{`Relatórios ${planFeatures?.basic?.advancedReports ? 'avançados' : 'simples'}`}</li>}
                <li><CheckCircle size={16} /> Suporte: {planFeatures?.basic?.supportLevel || 'standard'}</li>
              </ul>
            </div>

            <div className="plan plan-popular" style={{ '--i': 1 }}>
              <div className="plan-header">
                <Star className="plan-icon" />
                <h3>Pro</h3>
                <span className="popular-badge">Mais Popular</span>
              </div>
              <div className="price">{billingPeriod === 'monthly' ? formatCurrency(pricing.pro.monthly) : formatCurrency(pricing.pro.annual)}{billingPeriod === 'monthly' ? '/mês' : '/ano'}</div>
              <p>Mais recursos e tarifas reduzidas. <span className="fee-badge fee-pro">{systemFees.pro}%</span> <small className="fee-label">Taxa do sistema</small>.</p>
              <ul className="plan-features">
                {(!planFeatures?.pro?.maxReservationsPerMonth) ? (
                  <li><CheckCircle size={16} /> Reservas ilimitadas</li>
                ) : (
                  <li><CheckCircle size={16} /> Até {planFeatures.pro.maxReservationsPerMonth} reservas/mês</li>
                )}
                <li><CheckCircle size={16} /> Gestão avançada</li>
                {planFeatures?.pro?.advancedReports && <li><CheckCircle size={16} /> Relatórios avançados</li>}
                <li><CheckCircle size={16} /> Suporte: {planFeatures?.pro?.supportLevel || 'priority'}</li>
              </ul>
            </div>

            <div className="plan" style={{ '--i': 2 }}>
              <div className="plan-header">
                <Zap className="plan-icon" />
                <h3>Enterprise</h3>
              </div>
              <div className="price">{billingPeriod === 'monthly' ? formatCurrency(pricing.enterprise.monthly) : formatCurrency(pricing.enterprise.annual)}{billingPeriod === 'monthly' ? '/mês' : '/ano'}</div>
              <p>Alto volume — primeiras <span className="fee-pill">{systemFees.enterpriseFreeTransactions}</span> transações grátis/mês, depois <span className="fee-badge fee-enterprise">{systemFees.enterprise}%</span> <small className="fee-label">Taxa do sistema</small>.</p>
              <ul className="plan-features">
                {(!planFeatures?.enterprise?.maxReservationsPerMonth) ? (
                  <li><CheckCircle size={16} /> Reservas ilimitadas</li>
                ) : (
                  <li><CheckCircle size={16} /> Até {planFeatures.enterprise.maxReservationsPerMonth} reservas/mês</li>
                )}
                <li><CheckCircle size={16} /> {planFeatures?.enterprise?.apiAccess ? 'API personalizada' : 'Acesso API: não incluído'}</li>
                <li><CheckCircle size={16} /> Consultoria dedicada</li>
                <li><CheckCircle size={16} /> SLA garantido</li>
                <li><CheckCircle size={16} /> {planFeatures?.enterprise?.freeTransactionsPerMonth ?? systemFees.enterpriseFreeTransactions} transações grátis/mês</li>
              </ul>
            </div>
          </div>

          {Object.keys(serviceCommissions).length > 0 && (
            <div className="plans-commissions">
              <p><strong>Comissões padrão por tipo:</strong> {Object.entries(serviceCommissions).map(([k,v]) => `${k}: ${v}%`).join(' • ')}</p>
            </div>
          )}

        </div>
      </section>

      <section className="landing-how-it-works">
        <div className="how-it-works-inner">
          <h2>Como funciona o SDC Turismo</h2>
          <div className="steps-grid">
            <div className="step" style={{ '--i': 0 }}>
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Cadastre sua empresa</h4>
                <p>Preencha o formulário de pré-registro e aguarde aprovação do nosso time.</p>
              </div>
            </div>
            <div className="step" style={{ '--i': 1 }}>
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Configure seu plano</h4>
                <p>Escolha entre Básico, Pro ou Enterprise baseado no tamanho da sua operação.</p>
              </div>
            </div>
            <div className="step" style={{ '--i': 2 }}>
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Comece a usar</h4>
                <p>Gerencie reservas, pagamentos e relatórios com nossa plataforma completa.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="landing-features">
        <div className="features-inner">
          <h2>Por que escolher o SDC Turismo?</h2>
          <div className="features-grid">
            <div className="feature" style={{ '--i': 0 }}>
              <Calendar className="feature-icon" />
              <h4>Controle de Reservas</h4>
              <p>Calendário integrado e gestão completa de reservas em tempo real.</p>
            </div>
            <div className="feature" style={{ '--i': 1 }}>
              <BarChart3 className="feature-icon" />
              <h4>Relatórios Detalhados</h4>
              <p>Análises financeiras e de desempenho para tomada de decisões.</p>
            </div>
            <div className="feature" style={{ '--i': 2 }}>
              <Users className="feature-icon" />
              <h4>Gestão de Equipe</h4>
              <p>Controle de colaboradores, comissões e permissões personalizadas.</p>
            </div>
            <div className="feature" style={{ '--i': 3 }}>
              <Shield className="feature-icon" />
              <h4>Segurança Total</h4>
              <p>Dados protegidos com criptografia e backups automáticos.</p>
            </div>
            <div className="feature" style={{ '--i': 4 }}>
              <Zap className="feature-icon" />
              <h4>Links de Pagamento</h4>
              <p>Integração com Mercado Pago para pagamentos com split automático.</p>
            </div>
            <div className="feature" style={{ '--i': 5 }}>
              <Clock className="feature-icon" />
              <h4>Suporte 24/7</h4>
              <p>Equipe dedicada pronta para ajudar em qualquer momento.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="prereg" className="landing-prereg">
        <div className="prereg-inner">
          <div className="prereg-header">
            <div className="prereg-icon">
              <Send size={32} />
            </div>
            <h2>Solicitar pré-registro de Empresa</h2>
            <p>Junte-se a centenas de empresas que já confiam no SDC Turismo para gerenciar seus negócios.</p>
          </div>

          {message && (
            <div className={`prereg-message ${message.type}`}>
              <div className="message-icon">
                {message.type === 'success' ? <CheckCircle size={20} /> : <div className="error-icon">!</div>}
              </div>
              <span>{message.text}</span>
            </div>
          )}

          <form className="prereg-form modern-form" onSubmit={handleSubmit}>
            <div className="form-section">
              <h3>Informações da Empresa</h3>
              <div className="form-row two">
                <div className="input-group">
                  <label>
                    <Building size={16} />
                    Nome da Empresa *
                  </label>
                  <input
                    name="companyName"
                    value={form.companyName}
                    onChange={handleChange}
                    placeholder="Ex: Pousada do Sol Ltda"
                    required
                  />
                </div>
                <div className="input-group">
                  <label>
                    <Globe size={16} />
                    Website (opcional)
                  </label>
                  <input
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    placeholder="www.pousadadosol.com.br"
                    type="url"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Informações de Contato</h3>
              <div className="form-row two">
                <div className="input-group">
                  <label>
                    <User size={16} />
                    Nome do Contato *
                  </label>
                  <input
                    name="contactName"
                    value={form.contactName}
                    onChange={handleChange}
                    placeholder="João Silva"
                    required
                  />
                </div>
                <div className="input-group">
                  <label>
                    <Mail size={16} />
                    Email *
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="joao@pousadadosol.com.br"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="input-group">
                  <label>
                    <Phone size={16} />
                    Telefone (opcional)
                  </label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Plano Desejado</h3>
              <div className="plan-selector">
                <div className={`plan-option ${form.desiredPlan === 'basic' ? 'selected' : ''}`} onClick={() => setForm({...form, desiredPlan: 'basic'})}>
                  <div className="plan-option-header">
                    <CheckCircle className="plan-option-icon" />
                    <span>Básico</span>
                  </div>
                  <p>Ideal para empresas iniciantes</p>
                  <small>R$ {pricing.basic.monthly}/mês</small>
                </div>
                <div className={`plan-option ${form.desiredPlan === 'pro' ? 'selected' : ''}`} onClick={() => setForm({...form, desiredPlan: 'pro'})}>
                  <div className="plan-option-header">
                    <Star className="plan-option-icon" />
                    <span>Pro</span>
                    <span className="popular-tag">Mais Popular</span>
                  </div>
                  <p>Mais recursos e tarifas reduzidas</p>
                  <small>R$ {pricing.pro.monthly}/mês</small>
                </div>
                <div className={`plan-option ${form.desiredPlan === 'enterprise' ? 'selected' : ''}`} onClick={() => setForm({...form, desiredPlan: 'enterprise'})}>
                  <div className="plan-option-header">
                    <Zap className="plan-option-icon" />
                    <span>Enterprise</span>
                  </div>
                  <p>Alto volume e recursos avançados</p>
                  <small>R$ {pricing.enterprise.monthly}/mês</small>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? (
                  <>
                    <div className="loading-spinner"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Solicitar Pré-registro
                  </>
                )}
              </button>
              <div className="form-footer">
                <small className="form-note">
                  <Shield size={14} />
                  Seus dados estão seguros e criptografados
                </small>
                <small className="form-note">
                  <Clock size={14} />
                  Responderemos em até 2 dias úteis
                </small>
              </div>
            </div>
          </form>
        </div>
      </section>
      <div className={`lp-toast ${toast.visible ? 'visible' : ''}`}>{toast.text}</div>
    </div>
  );
};

export default Landing;
